import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Globals } from 'app/_globals';
import { CompetencyStatus, UserCompetency } from 'app/_models/competency.model';
import { COMPETENCY_CATEGORY_BUTTONS, MANAGE_WORKER_COMPETENCY_STATUS_BUTTONS, matchesCompetencyCategory, matchesCompetencyStatus } from 'app/_utils/competency-filters.utils';
import { SiteOwnerAuthorisation } from 'app/_models/site-owner-authorisation.model';
import { clearStoredSiteOwnerSelection, getStoredSiteOwnerSelection, setStoredSiteOwnerSelection } from 'app/_utils/site-owner-selection-storage.utils';
import { buildSummary, FilterSummaryItem } from 'app/_utils/filter-summary.utils';
import { canManageCompetencyForSseRoute, hasSseCompetencyRestrictions, isDelegateManagingSelf } from 'app/_utils/competency-permissions.utils';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { environment } from 'environments/environment';
import { CompetencyService } from 'app/_services/competency.service';
import { ContractorService } from 'app/_services/contractor.service';
import { NotificationService } from 'app/_services/notification.service';
import { TabItemHelperService } from 'app/_services/tab-item-helper.service';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { WorkerService } from 'app/_services/worker.service';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Component({
  selector: 'manage-worker-competencies',
  templateUrl: './manage-worker-competencies.component.html',
})
export class ManageWorkerCompetenciesComponent implements OnInit {
  public userId: number;
  public routeRole: string;
  public tabitems: any[] = [];
  public backLink: string;
  public selectedWorker: any;
  public competencySummary = '';
  public summarySaving = false;
  public competencies: UserCompetency[] = [];
  public workerHasVisibleSitesForSelectedOwner = false;
  public workerSiteAccessLoaded = false;
  public workerSiteAccessLoadFailed = false;

  public siteOwners: any[] = [];
  public selectedSiteOwner: any = null;
  public isAdminView: boolean = false;

  // Authorisation
  public authorisationNotes: SiteOwnerAuthorisation = null;
  public summaryEditMode = false;
  public summaryCreating = false;
  public newAuthorisationNotesText = '';

  public showAllCompetencies = false;

  public filters = {
    status: '',
    category: '',
  };

  public statusButtons = MANAGE_WORKER_COMPETENCY_STATUS_BUTTONS;

  public categoryButtons = COMPETENCY_CATEGORY_BUTTONS;
  public shouldShowCompetencyGuidance = false;
  public shouldShowFullAtGuidance = false;
  public shouldShowFullAtAndCtGuidance = false;

  public selectedWorkerId: number;
  public siteOwnerId: number | null = null;
  public siteOwnerName = '';
  private requestedSiteOwnerId: number | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private globals: Globals,
    private contractorService: ContractorService,
    private competencyService: CompetencyService,
    private notificationService: NotificationService,
    private tabItemHelperService: TabItemHelperService,
    private siteOwnerUserService: SiteOwnerUserService,
    private workerService: WorkerService,
  ) {
    this.userId = this.globals.user.id;
    this.routeRole = this.globals.user.routeRole;
    this.isAdminView = this.routeRole === 'admin' || this.routeRole === 'support';

    this.activatedRoute.queryParams.subscribe((params: Params) => {
      this.backLink = params.backLink;
      this.requestedSiteOwnerId = params.siteOwnerId ? parseInt(params.siteOwnerId, 10) : null;
    });

    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedWorkerId = parseInt(params.workerId, 10);
      this.refreshTabItems();
    });
  }

  ngOnInit() {
    this.loadWorker();
    this.getSiteOwnersList().subscribe((siteOwnerReady) => {
      if (siteOwnerReady) {
        this.onSiteOwnerReady();
      } else {
        this.workerSiteAccessLoaded = true;
      }
    });
  }

  private getSiteOwnersList() {
    const siteOwnerIds = this.globals.siteOwnerUsers.map(siteOwnerUser => siteOwnerUser.skylarkUserId);

    if (!this.isAdminView && siteOwnerIds.length === 1) {
      this.selectedSiteOwner = siteOwnerIds[0];
      this.siteOwnerId = siteOwnerIds[0];
      this.siteOwnerName = this.globals.siteOwnerUsers[0].name;
      return of(true);
    }

    return this.siteOwnerUserService.getAllEnabledSiteOwnersForDropdowns().pipe(
      map((result: any) => {
        this.siteOwners = result
          .filter(item => this.isAdminView || siteOwnerIds.includes(item.id))
          .map(item => ({
            label: item.firstName + ' ' + item.lastName,
            value: item.id,
          }));

        // from query param sent from a previous page
        const requestedSiteOwnerExists = this.siteOwners.some((siteOwner) => siteOwner.value === this.requestedSiteOwnerId);
        if (requestedSiteOwnerExists) {
          this.selectedSiteOwner = this.requestedSiteOwnerId;
          this.siteOwnerId = this.requestedSiteOwnerId;
          this.siteOwnerName = this.getSiteOwnerLabel(this.requestedSiteOwnerId);
          this.persistSelectedSiteOwner();
          return true;
        }

        const storedSiteOwnerId = this.getStoredSiteOwnerId();
        const storedSiteOwnerExists = this.siteOwners.some((siteOwner) => siteOwner.value === storedSiteOwnerId);

        if (storedSiteOwnerExists) {
          this.selectedSiteOwner = storedSiteOwnerId;
          this.siteOwnerId = storedSiteOwnerId;
          this.siteOwnerName = this.getSiteOwnerLabel(storedSiteOwnerId);
          return true;
        }

        this.clearSelectedSiteOwner();
        return false;
      }),
      catchError((error) => {
        console.error(error);
        this.notificationService.notify('danger', 'Error retrieving site owner list');
        this.clearSelectedSiteOwner();
        return of(false);
      })
    );
  }

  private loadWorker(): void {
    this.contractorService.getWorkerById(this.selectedWorkerId).subscribe((data: any) => {
      this.selectedWorker = data;
    });
  }

  public loadCompetencies(): void {
    if (!this.workerHasVisibleSitesForSelectedOwner) {
      this.competencies = [];
      this.updateCompetencyGuidance([]);
      return;
    }

    this.competencyService.loadUserCompetencyStatus(this.selectedWorkerId, this.siteOwnerId)
      .subscribe((workerCompetencies) => {
        this.competencies = workerCompetencies || [];
        this.updateCompetencyGuidance(this.competencies);
      });
  }

  public get selectedWorkerName(): string {
    return this.selectedWorker ? this.selectedWorker.firstName + ' ' + this.selectedWorker.lastName : '';
  }

  public get filteredCompetencies(): UserCompetency[] {
    return this.competencies.filter((competency) =>
      this.matchesMySite(competency) &&
      matchesCompetencyStatus(this.filters.status, competency) &&
      matchesCompetencyCategory(this.filters.category, competency)
    );
  }

  public get emptyCompetenciesMessage(): string {
    return this.competencies.length
      ? 'No competencies match the current filters'
      : 'No competencies added for this user';
  }

  public toggleMySiteFilter(): void {
    this.showAllCompetencies = !this.showAllCompetencies;
  }

  public setStatusFilter(key: string) {
    this.filters.status = key;
  }

  public setCategoryFilter(key: string) {
    this.filters.category = key;
  }

  public get activeFiltersSummary(): FilterSummaryItem[] {
    const summary = [];
    const statusSummary = buildSummary('Status', this.filters.status, this.statusButtons);
    const categorySummary = buildSummary('Category', this.filters.category, this.categoryButtons);

    if (statusSummary) {
      summary.push(statusSummary);
    }

    if (categorySummary) {
      summary.push(categorySummary);
    }

    return summary;
  }

  public onCardUpdated(competency: UserCompetency): void {
    this.loadCompetencies();
  }

  public onCardDeleted(competency: UserCompetency): void {
    this.competencyService.deleteCompetency(competency.id, this.siteOwnerId)
      .subscribe(
        () => {
          this.notificationService.notify('success', `${competency.name} removed for this site operator`);
          this.loadCompetencies();
        },
        () => this.notificationService.notify('danger', 'Error removing competency'),
      );
  }

  // A site delegate is a worker too. On their own record this page is read-only: they add and
  // submit their own competencies from 'My Competencies', and someone else has to approve them.
  public get isManagingSelfAsDelegate(): boolean {
    return isDelegateManagingSelf(this.globals.user, this.selectedWorkerId);
  }

  // Computed rather than set in the constructor because it depends on the route's workerId, which
  // is only available once the params subscription has run.
  public get canEditCompetencies(): boolean {
    return !!this.globals.user.canEditCompetencies && !this.isManagingSelfAsDelegate;
  }

  public canManageCompetency(competency: UserCompetency): boolean {
    if (!this.canEditCompetencies) {
      return false;
    }

    if (!hasSseCompetencyRestrictions(this.globals.user, this.siteOwnerId)) {
      return true;
    }

    return canManageCompetencyForSseRoute(
      competency,
      this.globals.user.canEditTurbineCompetencies,
      this.globals.user.canEditNonTurbineCompetencies,
    );
  }

  public get competencyScopeToggleLabel(): string {
    const selectedWorkerName = this.selectedWorkerName;

    if (!selectedWorkerName) {
      return '';
    }

    return `Include ${selectedWorkerName}'s competencies not linked to ${this.siteOwnerName || 'this Site Operator'}`;
  }

  private matchesMySite(competency: UserCompetency): boolean {
    if (this.showAllCompetencies) {
      return true;
    }
    return competency.hasCompetencyApproval;
  }

  private loadAuthorisationNotes(): void {
    if (!this.shouldShowAuthorisationRestrictions || !this.workerHasVisibleSitesForSelectedOwner) {
      this.authorisationNotes = null;
      return;
    }
    this.competencyService.getAuthorisationNotes(this.selectedWorkerId, this.siteOwnerId)
      .subscribe(notes => {
        this.authorisationNotes = notes && notes.length ? notes[0] : null;
      });
  }

  public createAuthorisationNotes(): void {
    const notes = (this.newAuthorisationNotesText || '').trim();
    if (!this.shouldShowAuthorisationRestrictions || !this.canEditAuthorisationRestrictions || !notes || this.summaryCreating) {
      return;
    }

    this.summaryCreating = true;
    const expiryDate = this.buildDefaultExpiryDate();
    this.competencyService.createAuthorisationNotes({
      siteOwnerSkylarkUserId: this.siteOwnerId,
      skylarkUserId: this.selectedWorkerId,
      competencyNotes: notes,
      competencyApprovalStatus: 'PENDING',
      expiryDate,
    }).subscribe(
      (created) => {
        this.summaryCreating = false;
        this.authorisationNotes = created;
        this.newAuthorisationNotesText = '';
        this.summaryEditMode = false;
        this.notificationService.notify('success', 'Competency restrictions created.');
      },
      () => {
        this.summaryCreating = false;
        this.notificationService.notify('danger', 'Error creating competency restrictions');
      }
    );
  }

  private buildDefaultExpiryDate(): string {
    const oneYearOut = new Date();
    oneYearOut.setFullYear(oneYearOut.getFullYear() + 1);
    return DateTimeUtils.localDateToTimelessString(oneYearOut);
  }

  public saveAuthorisationNotes(): void {
    if (!this.shouldShowAuthorisationRestrictions || !this.authorisationNotes || !this.canEditAuthorisationRestrictions || this.summarySaving) {
      return;
    }

    this.summarySaving = true;
    this.competencyService.updateAuthorisationNotes({
      siteOwnerAuthorisationId: this.authorisationNotes.id,
      siteOwnerSkylarkUserId: this.siteOwnerId,
      skylarkUserId: this.selectedWorkerId,
      competencyNotes: this.authorisationNotes.competencyNotes,
      competencyApprovalStatus: this.authorisationNotes.status,
      expiryDate: this.authorisationNotes.expiryDate,
    }).subscribe(
      () => {
        this.summarySaving = false;
        this.notificationService.notify('success', 'Competency restrictions saved.');
        this.summaryEditMode = false;
      },
      () => {
        this.summarySaving = false;
        this.notificationService.notify('danger', 'Error saving competency restrictions');
      }
    );
  }


  public selectedSiteOwnerChange(): void {
    this.siteOwnerId = this.selectedSiteOwner;
    this.siteOwnerName = this.getSiteOwnerLabel(this.siteOwnerId);
    this.persistSelectedSiteOwner();
    this.onSiteOwnerReady();
  }

  private getSiteOwnerLabel(siteOwnerId: number | null): string {
    const selectedSiteOwner = this.siteOwners.find((siteOwner) => siteOwner.value === siteOwnerId);
    return selectedSiteOwner ? selectedSiteOwner.label : '';
  }

  private getStoredSiteOwnerId(): number | null {
    return getStoredSiteOwnerSelection(this.userId);
  }

  private persistSelectedSiteOwner(): void {
    if (!this.siteOwnerId) {
      this.clearStoredSiteOwnerSelection();
      return;
    }

    setStoredSiteOwnerSelection(this.userId, this.siteOwnerId);
  }

  private clearSelectedSiteOwner(): void {
    this.siteOwnerId = null;
    this.selectedSiteOwner = null;
    this.siteOwnerName = '';
    this.competencies = [];
    this.updateCompetencyGuidance([]);
    this.workerHasVisibleSitesForSelectedOwner = false;
    this.workerSiteAccessLoadFailed = false;
    this.authorisationNotes = null;
    this.clearStoredSiteOwnerSelection();
  }

  private clearStoredSiteOwnerSelection(): void {
    clearStoredSiteOwnerSelection(this.userId);
  }

  private onSiteOwnerReady(): void {
    this.summaryEditMode = false;
    this.summarySaving = false;
    this.summaryCreating = false;
    this.newAuthorisationNotesText = '';
    this.authorisationNotes = null;

    this.updateWorkerSiteVisibilityForSelectedOwner();
  }

  private updateWorkerSiteVisibilityForSelectedOwner(): void {
    const requestedSiteOwnerId = this.siteOwnerId;

    this.workerSiteAccessLoaded = false;
    this.workerSiteAccessLoadFailed = false;

    this.workerService.getActiveSiteMembershipCount(requestedSiteOwnerId, this.selectedWorkerId).subscribe(
      (activeSiteCount: number) => {
        if (requestedSiteOwnerId !== this.siteOwnerId) {
          return;
        }

        this.workerHasVisibleSitesForSelectedOwner = Number(activeSiteCount) > 0;
        this.workerSiteAccessLoaded = true;
        this.loadCompetencies();
        this.loadAuthorisationNotes();
      },
      (error) => {
        if (requestedSiteOwnerId !== this.siteOwnerId) {
          return;
        }

        console.error(error);
        this.notificationService.notify('danger', 'Error retrieving worker site access');
        this.workerHasVisibleSitesForSelectedOwner = false;
        this.workerSiteAccessLoadFailed = true;
        this.workerSiteAccessLoaded = true;
      }
    );
  }

  public get shouldShowWorkerSiteAccessMessage(): boolean {
    return this.workerSiteAccessLoaded
      && !this.workerSiteAccessLoadFailed
      && !!this.siteOwnerId
      && !this.workerHasVisibleSitesForSelectedOwner;
  }

  private refreshTabItems(): void {
    if (!this.selectedWorkerId) {
      return;
    }

    this.tabitems = this.tabItemHelperService.getWorkerSectionTabItems(
      this.routeRole,
      this.backLink,
      this.selectedWorkerId,
      this.siteOwnerId,
    );
  }

  public get shouldShowAuthorisationRestrictions(): boolean {
    return Number(this.siteOwnerId) === environment.SSE_OWNER_SKYLARK_ID;
  }

  public get canEditAuthorisationRestrictions(): boolean {
    return this.shouldShowAuthorisationRestrictions && this.routeRole === 'authorising-officer';
  }

  public get workerSitesLink(): string[] {
    return ['/', this.routeRole, 'workers', String(this.selectedWorkerId), 'sites'];
  }

  public get workerSitesQueryParams(): any {
    const queryParams: any = {};

    if (this.backLink) {
      queryParams.backLink = this.backLink;
    }

    if (this.siteOwnerId) {
      queryParams.siteOwnerId = this.siteOwnerId;
    }

    return queryParams;
  }

  public get workerSiteAccessMessage(): string {
    if (this.routeRole === 'authorising-officer' || this.routeRole === 'power-station-manager') {
      return `This worker does not have access to any of ${this.siteOwnerName}'s sites. Please contact an administrator to have the user added to a site first.`;
    }

    return `This worker does not have access to any of ${this.siteOwnerName}'s sites. To manage their competencies, please add the user to a site first.`;
  }

  public get shouldShowWorkerSitesLink(): boolean {
    return this.routeRole !== 'authorising-officer' && this.routeRole !== 'power-station-manager';
  }

  private updateCompetencyGuidance(competencies: UserCompetency[]): void {
    const approvedCompetencies = (competencies || []).filter((competency) => competency.status === CompetencyStatus.Approved);
    const approvedFullAts = approvedCompetencies.filter((competency) =>
      competency.competencyTypeId === 1001
      && !competency.oem
      && !competency.model
    );
    const hasApprovedFullAt = approvedFullAts.length > 0;
    const hasApprovedCt = approvedCompetencies.some((competency) => competency.competencyTypeId === 2001);

    this.shouldShowFullAtGuidance = hasApprovedFullAt;
    this.shouldShowFullAtAndCtGuidance = hasApprovedFullAt && hasApprovedCt;
    this.shouldShowCompetencyGuidance = this.shouldShowFullAtGuidance || this.shouldShowFullAtAndCtGuidance;
  }

}
